import { collection, doc, setDoc, writeBatch } from 'firebase/firestore';
import { IComparisonPopulationSetting } from '../../../providers/settings/comparisonPopulationSettingsProvider';
import { IDateRange } from '../../../providers/settings/dates/dateRangeProvider';
import { ITsaReportSettings } from '../../../providers/settings/reports/tsaReportSettingsProvider';
import { ISettingsTemplate } from '../../../providers/settings/reports/types';
import { userAsPromise } from '../../../providers/userProvider';

/**
 * @description updates the tsa template
 * @param template
 * @param isSelected
 */
export const updateTsaTemplate = async (
  template: ISettingsTemplate<ITsaReportSettings>,
  isSelected: boolean
): Promise<void> => {
  const user = await userAsPromise();

  if (isSelected) {
    const batch = writeBatch(user.services.firestore);
    batch.set(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/tsaReport/templates/${template.id}`
      ),
      template,
      { merge: true }
    );
    batch.set(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/tsaReport`
      ),
      { selected: template },
      { merge: true }
    );
    await batch.commit();
  } else {
    await setDoc(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/tsaReport/templates/${template.id}`
      ),
      template,
      { merge: true }
    );
  }
};

/**
 * @description creates a new tsa report settings template
 * @param template
 */
export const createTsaTemplate = async (
  template: Partial<ISettingsTemplate<ITsaReportSettings>>
): Promise<void> => {
  const user = await userAsPromise();

  const docRef = doc(
    collection(
      user.services.firestore,
      `userProfiles/${user.id}/settings/tsaReport/templates`
    )
  );
  const newDocument = {
    ...template,
    id: docRef.id,
    active: true,
  };
  await setDoc(docRef, newDocument, { merge: true });
};

/**
 * @description saves the template as the selected template and
 * @param template
 */
export const saveTsaSettings = async (
  template: ISettingsTemplate<ITsaReportSettings>,
  targetTimeframe: IDateRange,
  comparisonPopulation: IComparisonPopulationSetting
): Promise<void> => {
  const user = await userAsPromise();

  const docRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/tsaReport`
  );

  await setDoc(
    docRef,
    {
      selected: template ?? null,
      targetTimeframe: targetTimeframe ?? null,
      comparisonPopulation,
    },
    { merge: true }
  );
};

/**
 * @description delete the template and change the current template to another one
 * @param template
 * @param replacement
 * @returns
 */
export const deleteSelectedTemplate = async (
  template: ISettingsTemplate<ITsaReportSettings>,
  replacement: ISettingsTemplate<ITsaReportSettings>
): Promise<void> => {
  const user = await userAsPromise();

  const batch = writeBatch(user.services.firestore);
  const updateRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/tsaReport/templates/${template.id}`
  );
  const update: ISettingsTemplate<ITsaReportSettings> = {
    ...template,
    active: false,
  };
  batch.update(updateRef, update as unknown as Record<string, unknown>);
  const selectedRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/tsaReport`
  );
  batch.set(selectedRef, { selected: replacement ?? null }, { merge: true });
  return batch.commit();
};
